export type SystemConfigGroupKey =
  | 'general'
  | 'lead'
  | 'customer'
  | 'opportunity'
  | 'product'
  | 'marketing'
  | 'contract';

export type SystemConfigItemKey =
  | 'trip-close-reasons'
  | 'taxonomies'
  | 'automation-rules'
  | 'permissions'
  | 'workflow-approval'
  | 'invoice-profile'
  | 'ai-model-binding'
  | 'tavily-config'
  | 'dictionary-management'
  | 'lead-pool'
  | 'lead-assign-rules'
  | 'source-channels'
  | 'customer-levels'
  | 'industries'
  | 'areas'
  | 'customer-pool'
  | 'customer-limits'
  | 'opportunity-pipelines'
  | 'opportunity-stages'
  | 'marketing-campaign-types'
  | 'marketing-touchpoint-types'
  | 'contract-config'
  | 'contract-code-rules';

export type SystemConfigItem = {
  key: SystemConfigItemKey;
  path: string;
  label: string;
  localeId: string;
  description?: string;
  resourceCode?: string;
};

import { PERMISSION_RESOURCE_CODES } from '@/permissions/definitions';

export type SystemConfigGroup = {
  key: SystemConfigGroupKey;
  label: string;
  localeId: string;
  description: string;
  items: SystemConfigItem[];
};

export const SYSTEM_CONFIG_GROUPS: SystemConfigGroup[] = [
  {
    key: 'general',
    label: '系统通用',
    localeId: 'menu.zy-crm.systemConfig.general',
    description: '集中维护跨业务对象共用的权限、自动化、模型和发票档案能力。',
    items: [
      {
        key: 'permissions',
        path: '/setting/general/permission',
        label: '权限配置',
        localeId: 'menu.zy-crm.systemConfig.permissions',
        resourceCode: PERMISSION_RESOURCE_CODES.settingsPermissions,
      },
      {
        key: 'trip-close-reasons',
        path: '/setting/business-trip/close-reasons',
        label: '出差事项关闭理由',
        localeId: 'menu.zy-crm.systemConfig.tripCloseReasons',
        resourceCode: 'business-trips:manage-close-reasons',
      },
      {
        key: 'taxonomies',
        path: '/setting/general/taxonomy',
        label: '标签管理',
        localeId: 'menu.zy-crm.systemConfig.taxonomies',
        resourceCode: PERMISSION_RESOURCE_CODES.settingsTaxonomies,
      },
      {
        key: 'automation-rules',
        path: '/setting/general/automation-rule',
        label: '自动化规则',
        localeId: 'menu.zy-crm.systemConfig.automationRules',
        resourceCode: PERMISSION_RESOURCE_CODES.settingsAutomationRules,
      },
      {
        key: 'workflow-approval',
        path: '/setting/general/workflow-approval',
        label: '审批配置',
        localeId: 'menu.zy-crm.systemConfig.workflowApproval',
        resourceCode: PERMISSION_RESOURCE_CODES.settingsWorkflowApproval,
      },
      {
        key: 'invoice-profile',
        path: '/setting/general/invoice-profile',
        label: '发票信息',
        localeId: 'menu.zy-crm.systemConfig.invoiceProfile',
        resourceCode: PERMISSION_RESOURCE_CODES.settingsInvoiceProfile,
      },
      {
        key: 'ai-model-binding',
        path: '/setting/general/ai-model-binding',
        label: '模型设置',
        localeId: 'menu.zy-crm.systemConfig.modelSettings',
        resourceCode: 'modelSettings',
      },
      {
        key: 'tavily-config',
        path: '/setting/general/tavily',
        label: 'Tavily 设置',
        localeId: 'menu.zy-crm.systemConfig.tavily',
        resourceCode: 'modelSettings',
      },
      {
        key: 'dictionary-management',
        path: '/setting/general/dictionary',
        label: '数据字典',
        localeId: 'menu.zy-crm.systemConfig.dictionaryManagement',
        resourceCode: PERMISSION_RESOURCE_CODES.settingsDictionaries,
      },
    ],
  },
  {
    key: 'lead',
    label: '线索设置',
    localeId: 'menu.zy-crm.systemConfig.lead',
    description:
      '把线索规则与业务选项放到一个入口里统一维护，业务上仍按线索语境配置。',
    items: [
      {
        key: 'lead-pool',
        path: '/setting/lead/pool',
        label: '线索池配置',
        localeId: 'menu.zy-crm.systemConfig.leadPoolConfigs',
        resourceCode: PERMISSION_RESOURCE_CODES.settingsLeadPool,
      },
      {
        key: 'lead-assign-rules',
        path: '/setting/lead/assign-rule',
        label: '线索分配规则',
        localeId: 'menu.zy-crm.systemConfig.leadAssignRules',
        resourceCode: PERMISSION_RESOURCE_CODES.settingsLeadAssignRules,
      },
      {
        key: 'industries',
        path: '/setting/lead/industry',
        label: '行业配置',
        localeId: 'menu.zy-crm.systemConfig.industries',
        resourceCode: PERMISSION_RESOURCE_CODES.settingsIndustries,
      },
      {
        key: 'areas',
        path: '/setting/lead/area',
        label: '地区配置',
        localeId: 'menu.zy-crm.systemConfig.areas',
        resourceCode: PERMISSION_RESOURCE_CODES.settingsAreas,
      },
    ],
  },
  {
    key: 'customer',
    label: '客户设置',
    localeId: 'menu.zy-crm.systemConfig.customer',
    description: '将客户资料选项、公海和私池上限统一收口到客户设置。',
    items: [
      {
        key: 'customer-pool',
        path: '/setting/customer/pool',
        label: '客户公海配置',
        localeId: 'menu.zy-crm.systemConfig.customerPoolConfigs',
        resourceCode: PERMISSION_RESOURCE_CODES.settingsCustomerPool,
      },
      {
        key: 'customer-limits',
        path: '/setting/customer/limit',
        label: '私池上限配置',
        localeId: 'menu.zy-crm.systemConfig.customerLimitConfigs',
        resourceCode: PERMISSION_RESOURCE_CODES.settingsCustomerLimits,
      },
      {
        key: 'source-channels',
        path: '/setting/customer/source-channel',
        label: '来源渠道配置',
        localeId: 'menu.zy-crm.systemConfig.sourceChannels',
        resourceCode: PERMISSION_RESOURCE_CODES.settingsSourceChannels,
      },
      {
        key: 'customer-levels',
        path: '/setting/customer/level',
        label: '客户等级配置',
        localeId: 'menu.zy-crm.systemConfig.customerLevels',
        resourceCode: PERMISSION_RESOURCE_CODES.settingsCustomerLevels,
      },
    ],
  },
  {
    key: 'opportunity',
    label: '商机设置',
    localeId: 'menu.zy-crm.systemConfig.opportunity',
    description:
      '商机场景下分别维护销售流程与阶段规则，底层使用统一选项与扩展表。',
    items: [
      {
        key: 'opportunity-pipelines',
        path: '/setting/opportunity/pipeline',
        label: '商机管道',
        localeId: 'menu.zy-crm.systemConfig.opportunityPipelines',
        resourceCode: PERMISSION_RESOURCE_CODES.settingsOpportunityPipelines,
      },
      {
        key: 'opportunity-stages',
        path: '/setting/opportunity/stage',
        label: '商机阶段',
        localeId: 'menu.zy-crm.systemConfig.opportunityStages',
        resourceCode: PERMISSION_RESOURCE_CODES.settingsOpportunityStages,
      },
    ],
  },
  {
    key: 'marketing',
    label: '市场活动设置',
    localeId: 'menu.zy-crm.systemConfig.marketing',
    description:
      '统一维护市场活动业务类型和接入点类型，供活动创建与接入点管理复用。',
    items: [
      {
        key: 'marketing-campaign-types',
        path: '/setting/marketing/campaign-type',
        label: '活动类型',
        localeId: 'menu.zy-crm.systemConfig.marketingCampaignTypes',
        resourceCode: PERMISSION_RESOURCE_CODES.settingsMarketingCampaignTypes,
      },
      {
        key: 'marketing-touchpoint-types',
        path: '/setting/marketing/touchpoint-type',
        label: '接入点类型',
        localeId: 'menu.zy-crm.systemConfig.marketingTouchpointTypes',
        resourceCode:
          PERMISSION_RESOURCE_CODES.settingsMarketingTouchpointTypes,
      },
    ],
  },
  {
    key: 'contract',
    label: '合同设置',
    localeId: 'menu.zy-crm.systemConfig.contract',
    description: '合同提醒规则和合同编号规则采用同一组设置页面承载。',
    items: [
      {
        key: 'contract-config',
        path: '/setting/contract/config',
        label: '合同配置',
        localeId: 'menu.zy-crm.systemConfig.contractConfigs',
        resourceCode: PERMISSION_RESOURCE_CODES.settingsContractConfig,
      },
      {
        key: 'contract-code-rules',
        path: '/setting/contract/code-rule',
        label: '合同编号规则',
        localeId: 'menu.zy-crm.systemConfig.contractCodeRules',
        resourceCode: PERMISSION_RESOURCE_CODES.settingsContractCodeRules,
      },
    ],
  },
];

export const SYSTEM_CONFIG_GROUP_DEFAULT_PATHS = Object.fromEntries(
  SYSTEM_CONFIG_GROUPS.map((group) => [
    group.key,
    group.items[0]?.path || '/setting',
  ]),
) as Record<SystemConfigGroupKey, string>;

export const SYSTEM_CONFIG_ITEMS = SYSTEM_CONFIG_GROUPS.flatMap((group) =>
  group.items.map((item) => ({ ...item, groupKey: group.key })),
) as Array<SystemConfigItem & { groupKey: SystemConfigGroupKey }>;

export const SYSTEM_CONFIG_ITEM_PATHS = Object.fromEntries(
  SYSTEM_CONFIG_ITEMS.map((item) => [item.key, item.path]),
) as Record<SystemConfigItemKey, string>;

function normalizeSystemConfigPath(pathname: string) {
  const basePath = pathname.replace(/^\/zy-crm/, '') || '/';

  if (basePath.startsWith('/system-config/')) {
    return `/setting/${basePath.slice('/system-config/'.length)}`;
  }

  if (basePath === '/system-config') {
    return SYSTEM_CONFIG_GROUP_DEFAULT_PATHS.general;
  }

  if (basePath === '/setting') {
    return SYSTEM_CONFIG_GROUP_DEFAULT_PATHS.general;
  }

  return basePath;
}

export function resolveSystemConfigByPath(pathname: string) {
  const normalizedPath = normalizeSystemConfigPath(pathname);
  const matchedItem = SYSTEM_CONFIG_ITEMS.find(
    (item) => item.path === normalizedPath,
  );
  if (!matchedItem) {
    return null;
  }

  const group = SYSTEM_CONFIG_GROUPS.find(
    (candidate) => candidate.key === matchedItem.groupKey,
  );
  if (!group) {
    return null;
  }

  return {
    group,
    item:
      group.items.find((entry) => entry.key === matchedItem.key) ||
      group.items[0],
  };
}
